import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useHistory } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import ReactBSAlert from 'react-bootstrap-sweetalert'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import CardContent from '@material-ui/core/CardContent'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import FormGroup from '@material-ui/core/FormGroup'
import FormControl from '@material-ui/core/FormControl'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Button from '@material-ui/core/Button'
import Collapse from '@material-ui/core/Collapse'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import Loader from 'components/general/Loader'
import AdminAPI from 'config/api/AdminAPI'
import EnergyAPI from 'config/api/EnergyAPI'
import { onError } from 'config/lib/errorLib'
import ValidationList from 'components/admin/ValidationList'
import AutoAllocationRules from 'components/admin/AutoAllocationRules'
import CompaniesTable from 'components/admin/CompaniesTable'
import SolarCompaniesTable from 'components/admin/Energy/CompaniesTable'
import DncCard from 'components/admin/DncCard'

import componentStyles from 'assets/theme/components/cards/cards/card-button.js'

const useStyles = makeStyles(componentStyles)

function Settings() {
    const api = new AdminAPI()
    const eApi = new EnergyAPI()
    const history = useHistory()

    const classes = { ...useStyles() }

    const [validationLists, setValidationLists] = useState(null)
    const [allocationRules, setAllocationRules] = useState({})
    const [showAllocationGuide, setShowAllocationGuide] = useState(false)
    const [showStatus, setShowStatus] = useState('active')
    const [companies, setCompanies] = useState([])
    const [solarCompanies, setSolarCompanies] = useState([])
    const [showCompanies, setShowCompanies] = useState([])
    const [showSolarCompanies, setShowSolarCompanies] = useState([])
    const [loading, setLoading] = useState(true)
    const [alert, setAlert] = useState(false)

    useEffect(() => {
        getSettings()
    }, [])

    async function getSettings() {
        try {
            const lists = await api.getValidationLists()
            console.log('validation lists: ', lists)

            const [lifeRules, ipRules] = await Promise.all([
                api.getLifeAllocationRules(),
                api.getIncomeProtectionAllocationRules()
            ])

            const companies = await api.getCompanies()
            console.log('companies: ', companies)
            companies.sort((a, b) => {
                return a.name.localeCompare(b.name)
            })
            const activeCompanies = companies.filter((company) => (company.active))

            const solarCompanies = await eApi.getCompanies()
            console.log('solarCompanies: ', solarCompanies)
            solarCompanies.sort((a, b) => {
                return a.name.localeCompare(b.name)
            })
            const activeSolarCompanies = solarCompanies.filter((company) => (company.active))

            setValidationLists(lists)
            setAllocationRules({ 'life-insurance': lifeRules, 'income-protection': ipRules })
            setCompanies(companies)
            setSolarCompanies(solarCompanies)
            setShowCompanies(activeCompanies)
            setShowSolarCompanies(activeSolarCompanies)
            setLoading(false)
        } catch(e) {
            onError(e)
        }
    }

    function updateCompany(company) {
        const index = companies.findIndex(oldCompany => (oldCompany.companyId === company.companyId))
        console.log('company index: ', index)

        let newCompanies = [...companies]
        newCompanies[index] = company
        let checkStatus = showStatus === 'active' ? true : false
        let showCompanies = newCompanies.filter((company) => (company.active === checkStatus))
        console.log(showCompanies)
        setCompanies(newCompanies)
        setShowCompanies(showCompanies)
    }

    function updateSolarCompany(company) {
        const index = solarCompanies.findIndex(oldCompany => (oldCompany.companyId === company.companyId))
        console.log('solar company index: ', index)

        let newSolarCompanies = [...solarCompanies]
        newSolarCompanies[index] = company
        let checkStatus = showStatus === 'active' ? true : false
        let showSolarCompanies = newSolarCompanies.filter((company) => (company.active === checkStatus))
        console.log(showSolarCompanies)
        setSolarCompanies(newSolarCompanies)
        setShowSolarCompanies(showSolarCompanies)
    }

    function renderSwitchStatus(product, label) {
        const r = allocationRules[product]
        if (!r) return null
        const onOff = (on) => (
            <Box component='span' fontWeight={600} color={on ? 'success.main' : 'error.main'}>{on ? 'On' : 'Off'}</Box>
        )
        const reject = r.ageRejection || {}
        const rejectText = [reject.underMin && `under ${r.ageRange?.min}`, reject.overMax && `over ${r.ageRange?.max}`].filter(Boolean).join(' & ')
        return (
            <Typography variant='body2' color='textSecondary'>
                <Box component='span' fontWeight={600} color='text.primary'>{label}:</Box>{' '}
                auto allocation {onOff(r.enabled !== false)} &middot; cover site leads {onOff(r.enabled !== false && r.coverAllocation?.enabled !== false)} &middot;
                queue {onOff(r.enabled !== false && r.queue?.enabled !== false)} &middot; age auto-reject {rejectText ? <Box component='span' fontWeight={600} color='text.primary'>{rejectText}</Box> : onOff(false)}
            </Typography>
        )
    }

    function updateShowStatus(showStatus) {
        let checkStatus = showStatus === 'active' ? true : false
        let showCompanies = companies.filter((company) => (company.active === checkStatus))
        let showSolarCompanies = solarCompanies.filter((company) => (company.active === checkStatus))
        setShowCompanies(showCompanies)
        setShowSolarCompanies(showSolarCompanies)
        setShowStatus(showStatus)
    }

    function showSuccessAlert(text) {
        setAlert(
            <ReactBSAlert
                success
                style={{ display: 'block', marginTop: '-100px' }}
                title='Success'
                onConfirm={() => setAlert(false)}
                onCancel={() => setAlert(false)}
                confirmBtnBsStyle='success'
                confirmBtnText='Ok'
                btnSize=''
                confirmBtnStyle={{
                    marginRight: undefined,
                    borderColor: undefined,
                    boxShadow: undefined,
                }}
            >
                {text}
            </ReactBSAlert>
        )
    }

    return (
        <>
            <Container
                maxWidth={false}
                component={Box}
                marginTop='2rem'
                classes={{ root: classes.containerRoot }}
            >
                {!loading ? (
                    <Grid container>
                        <Grid item xs={12}>
                            <Box display='flex' justifyContent='flex-end' marginBottom='1rem'>
                                <Button
                                    variant='contained'
                                    color='primary'
                                    onClick={() => history.push('/export-leads')}
                                >
                                    Export Leads
                                </Button>
                            </Box>
                        </Grid>
                        <Grid item xs={12} xl={12}>
                            <Card classes={{ root: classes.cardRoot }}>
                                <CardHeader
                                    subheader={
                                        <Box component={Typography} variant='h3' marginBottom='0!important'>
                                            How Automatic Allocation Works
                                        </Box>
                                    }
                                />
                                <CardContent>
                                    <Box display='flex' alignItems='flex-end' justifyContent='space-between' flexWrap='wrap'>
                                        <Box>
                                            {renderSwitchStatus('life-insurance', 'Life Insurance')}
                                            {renderSwitchStatus('income-protection', 'Income Protection')}
                                        </Box>
                                        <Button
                                            size='small'
                                            color='primary'
                                            onClick={() => setShowAllocationGuide(!showAllocationGuide)}
                                            endIcon={showAllocationGuide ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        >
                                            {showAllocationGuide ? 'Hide guide' : 'How it works'}
                                        </Button>
                                    </Box>
                                    <Collapse in={showAllocationGuide}>
                                    <Grid container spacing={4} style={{ marginTop: '0.5rem' }}>
                                        <Grid item xs={12} md={4}>
                                            <Typography variant='h5' color='textPrimary' gutterBottom style={{ fontSize: '1.1rem' }}>
                                                When leads are queued
                                            </Typography>
                                            <Divider style={{ marginBottom: '1rem' }} />
                                            <Typography variant='body2' color='textSecondary'>
                                                When a Life or Income Protection lead is eligible but can&apos;t be placed
                                                because every buyer has hit their weekly cap, it is queued instead of left
                                                unallocated. Queued leads show a{' '}
                                                <Box component='span' fontWeight={600} color='text.primary'>Queued &rarr; buyer</Box>{' '}
                                                badge on the leads dashboard so you can see where each one is headed. When the
                                                queue is switched off for a product, these leads are left unallocated instead.
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Typography variant='h5' color='textPrimary' gutterBottom style={{ fontSize: '1.1rem' }}>
                                                When the queue is released
                                            </Typography>
                                            <Divider style={{ marginBottom: '1rem' }} />
                                            <Typography variant='body2' color='textSecondary'>
                                                Queued leads are released gradually and at random across two windows so buyers
                                                aren&apos;t hit with a sudden batch: <Box component='span' fontWeight={600} color='text.primary'>Life
                                                Insurance on Sunday, 6pm&ndash;10pm</Box> and <Box component='span' fontWeight={600} color='text.primary'>Income
                                                Protection on Monday, 8am&ndash;12pm</Box>. Within each window they&apos;re allocated in
                                                your order of preference (priority buyers &rarr; large buyers &rarr; small buyers
                                                with the most weekly room), respecting weekly caps, de-duplication and attribute
                                                rules (age, risk class, salary). Time-of-day, day-of-week and rate-limit rules are
                                                ignored, and no buyer is ever over-allocated. The release is skipped when a
                                                product&apos;s auto allocation or queue is switched off; queued leads keep their badge.
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Typography variant='h5' color='textPrimary' gutterBottom style={{ fontSize: '1.1rem' }}>
                                                Leftovers &amp; weekends
                                            </Typography>
                                            <Divider style={{ marginBottom: '1rem' }} />
                                            <Typography variant='body2' color='textSecondary'>
                                                Anything that still can&apos;t be placed by the end of a window stays unallocated
                                                and is emailed to Declan with a per-company summary. Weekend leads are not queued
                                                &mdash; they simply appear in &ldquo;This Week&rsquo;s&rdquo; unallocated list at the
                                                start of the week. Manually rejecting or allocating a queued lead removes it from
                                                the queue.
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Typography variant='h5' color='textPrimary' gutterBottom style={{ fontSize: '1.1rem' }}>
                                                Cover site leads
                                            </Typography>
                                            <Divider style={{ marginBottom: '1rem' }} />
                                            <Typography variant='body2' color='textSecondary'>
                                                Leads from <Box component='span' fontWeight={600} color='text.primary'>cover.lifecompare.ie</Box> and{' '}
                                                <Box component='span' fontWeight={600} color='text.primary'>cover.insureyourincome.ie</Box> are
                                                auto-allocated to <Box component='span' fontWeight={600} color='text.primary'>Actual Insurances only</Box>.
                                                If Actual already has the phone number they go to the next large buyer; if Actual is at
                                                cap they are queued for Actual. Only the age (and for IP, risk class and salary) rules
                                                apply &mdash; business hours, weekends, daily and rate limits do not. Each product has its
                                                own cover switch, and the product&apos;s main switch turns cover allocation off too.
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Typography variant='h5' color='textPrimary' gutterBottom style={{ fontSize: '1.1rem' }}>
                                                Health leads from the life form
                                            </Typography>
                                            <Divider style={{ marginBottom: '1rem' }} />
                                            <Typography variant='body2' color='textSecondary'>
                                                Life form visitors who ask for a health insurance quote are saved as{' '}
                                                <Box component='span' fontWeight={600} color='text.primary'>Health Insurance</Box> leads and
                                                auto-allocated straight away to the priority health buyer, or{' '}
                                                <Box component='span' fontWeight={600} color='text.primary'>CFS</Box> if there isn&apos;t one.
                                                This is <Box component='span' fontWeight={600} color='text.primary'>not affected by the Life
                                                Insurance switches</Box>. Visitors who ask for both are treated as life leads.
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Typography variant='h5' color='textPrimary' gutterBottom style={{ fontSize: '1.1rem' }}>
                                                Age auto-reject
                                            </Typography>
                                            <Divider style={{ marginBottom: '1rem' }} />
                                            <Typography variant='body2' color='textSecondary'>
                                                Leads whose age is outside the product&apos;s age range are left unallocated by
                                                default. Turning on auto-reject moves them straight to{' '}
                                                <Box component='span' fontWeight={600} color='text.primary'>Rejected</Box> with the age as the
                                                reason. This runs on every new lead even when auto allocation is off. Leads with a
                                                missing or unreadable date of birth are never auto-rejected, and the website still
                                                shows the visitor the normal confirmation.
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    </Collapse>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} xl={12}>
                            <AutoAllocationRules
                                companies={companies}
                                onRulesSaved={(product, rules) => setAllocationRules(prev => ({ ...prev, [product]: rules }))}
                            />
                        </Grid>
                        <Grid item xs={12} xl={12}>
                            <DncCard />
                        </Grid>
                        <Grid item xs={12} xl={12}>
                            <Card
                                classes={{
                                    root: classes.cardRoot,
                                }}
                            >
                                <CardHeader
                                    subheader={
                                        <Grid
                                            container
                                            component={Box}
                                            alignItems='center'
                                            justifyContent='space-between'
                                        >
                                            <Grid item xs='auto'>
                                                <Box
                                                    component={Typography}
                                                    variant='h3'
                                                    marginBottom='0!important'
                                                >
                                                    Lead Validation
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    }
                                    classes={{ root: classes.cardHeaderRoot }}
                                >
                                </CardHeader>
                                
                                <CardContent>
                                    <Grid container spacing={1}>
                                        <Grid item xs={12} md={4}>  
                                            <ValidationList 
                                                title={'Profanities (Not Allowed)'}
                                                list={'profanities'}
                                                items={validationLists.profanities.join('\n')}
                                                updateLists={(lists) => setValidationLists(lists)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>  
                                            <ValidationList 
                                                title={'Fake Names (Not Allowed)'}
                                                list={'fakeNames'}
                                                items={validationLists.fakeNames.join('\n')}
                                                updateLists={(lists) => setValidationLists(lists)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>  
                                            <ValidationList 
                                                title={'Famous Names (Not Allowed)'}
                                                list={'famousNames'}
                                                items={validationLists.famousNames.join('\n')}
                                                updateLists={(lists) => setValidationLists(lists)}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container style={{marginTop: '2rem'}}>
                                        <Grid item xs={12}>
                                            <h3>Active Validation Checks</h3>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box style={{color: '#525f7f'}}>
                                                <h4 style={{marginBottom: '0.5rem', marginTop: '0.5rem'}}>Name & Profanity Checks</h4>
                                                <ul>
                                                    <li>Full Name checked for exact match to Fake Names & Famous Names</li>
                                                    <li>First Name checked for exact match to Profanities</li>
                                                    <li>Last Name checked for exact match to Profanities</li>
                                                    <li>Email prefix (before @) checked for exact match to Profanities</li>
                                                    <li>Rejected if <strong>both</strong> First Name and Last Name are a single letter (e.g. "X Z"). A single-letter first name with a real surname (e.g. "K Hogan") is still allowed.</li>
                                                </ul>

                                                <h4 style={{marginBottom: '0.5rem', marginTop: '1rem'}}>Phone Number Checks</h4>
                                                <p style={{marginBottom: '0.25rem'}}><em>Skipped for phone-verified sources: quotes.insureyourincome.ie, lifecompare.ie, solarfinder.ie</em></p>
                                                <ul>
                                                    <li>International Irish prefixes (00353, 0353, 353) are normalised to local format (0xx)</li>
                                                    <li>Numbers without a leading 0 automatically get one added</li>
                                                    <li><strong>Irish mobiles accepted:</strong> 083, 085, 086, 087, 089</li>
                                                    <li><strong>Dublin landlines accepted:</strong> 01</li>
                                                    <li><strong>Other Irish landlines accepted:</strong> 021–029, 041–049, 051–059, 061–069, 071, 074, 076, 090–099</li>
                                                    <li>Phone rejected if 5+ repeated digits in a row (e.g. 11111)</li>
                                                    <li>Phone rejected if 5+ sequential ascending digits (e.g. 12345)</li>
                                                </ul>

                                                <h4 style={{marginBottom: '0.5rem', marginTop: '1rem'}}>Duplicate Detection</h4>
                                                <p style={{marginBottom: '0.25rem'}}>Duplicates are matched by <strong>phone number</strong> + <strong>same product</strong> within a lookback window.</p>
                                                <ul>
                                                    {(() => {
                                                        const dw = validationLists.duplicateWindows;
                                                        const productLabels = {
                                                            'home-insurance': 'Home insurance',
                                                            'life-insurance': 'Life insurance',
                                                            'income-protection': 'Income protection',
                                                            'mortgage-protection': 'Mortgage protection',
                                                            'health-insurance': 'Health insurance',
                                                            'car-insurance': 'Car insurance',
                                                            'business-insurance': 'Business insurance',
                                                            'pensions': 'Pensions'
                                                        }
                                                        if (!dw) {
                                                            return (
                                                                <li><strong>Financial Services products</strong> (pensions, life, home, mortgage, health, car, business, income protection, etc.) — <strong>60-day</strong> window. Duplicates are <strong>rejected</strong>.</li>
                                                            )
                                                        }
                                                        const overrides = dw.overrides || {}
                                                        return (
                                                            <>
                                                                <li><strong>Financial Services products</strong> (life, pensions, mortgage, health, car, business, income protection, etc.) — <strong>{dw.default}-day</strong> window. Duplicates are <strong>rejected</strong>.</li>
                                                                {Object.keys(overrides).map((product) => (
                                                                    <li key={product}><strong>{productLabels[product] || product}</strong> — <strong>{overrides[product]}-day</strong> window. Duplicates are <strong>rejected</strong>.</li>
                                                                ))}
                                                            </>
                                                        )
                                                    })()}
                                                    <li><strong>Solar / Energy products</strong> (Solar, Commercial Solar, Heat Pump, Boiler, EV Charger, Insulation) — <strong>7-day</strong> window, checked <strong>across both EE and SF</strong> (a person coming through from another source counts as a duplicate).</li>
                                                    <li><strong>Energy duplicates are no longer rejected.</strong> Within the 7-day window the lead is re-sent to the <strong>same company it was originally allocated to</strong>, with <em>(duplicate)</em> appended to the last name.</li>
                                                    <li>Energy <em>(duplicate)</em> leads are <strong>free</strong> — no balance is charged and they do <strong>not</strong> count towards a provider's weekly/monthly totals (deliveries screen, invoices, caps).</li>
                                                    <li>If the original company no longer exists or has no delivery set up for that product, the lead is allocated <strong>as normal</strong> to an eligible buyer with no <em>(duplicate)</em> tag and counts normally. (A paused/inactive delivery still receives the duplicate within the window.)</li>
                                                    <li>Outside the 7-day window an energy lead is treated as a fresh lead and allocated as normal.</li>
                                                    <li>Duplicate check matches against <strong>valid leads only</strong> — previously rejected leads are excluded</li>
                                                    <li>If a lead was rejected (failed validation, profanity, fake name, etc.), it will <strong>not</strong> block resubmission</li>
                                                    <li>Email is <strong>not</strong> used for duplicate detection — only phone number</li>
                                                </ul>

                                                <h4 style={{marginBottom: '0.5rem', marginTop: '1rem'}}>Tables Checked</h4>
                                                <ul>
                                                    <li><strong>Energy / Solar products</strong> are checked against <strong>both bd-ee-all-leads and bd-sf-all-leads</strong> (cross-source duplicate detection)</li>
                                                    <li><strong>energyefficiency.ie</strong> (non-energy) leads checked against bd-ee-all-leads</li>
                                                    <li><strong>solarfinder.ie / quotes.solarfinder.ie</strong> (non-energy) leads checked against bd-sf-all-leads</li>
                                                    <li><strong>All other sources</strong> checked against bd-all-leads</li>
                                                </ul>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid
                                container
                                component={Box}
                                alignItems='center'
                                justifyContent='flex-start'
                                style={{marginBottom: '2rem'}}
                                direction='row'
                            >
                                <Grid item xs={12} md={2}>
                                    <FormGroup style={{marginBottom: '0'}}>
                                        <FormControl variant='outlined' fullWidth>
                                            <Select
                                                multiple={false}
                                                value={showStatus}
                                                onChange={(e) => updateShowStatus(e.target.value)}
                                            >
                                                <MenuItem value='active'>Active</MenuItem>
                                                <MenuItem value='inactive'>Inactive</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </FormGroup>
                                </Grid>
                                {/*
                                <Grid item xs={2}>
                                    <a href={getXeroLink()}>Link Xero</a>
                                </Grid>
                                */}
                            </Grid>
                        </Grid>
                        <Grid item xs={6}>
                            <CompaniesTable
                                companies={showCompanies}
                                updateCompany={(company) => updateCompany(company)}
                                refreshCompanies={() => getSettings()}
                                showSuccessAlert={(text) => showSuccessAlert(text)}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <SolarCompaniesTable
                                companies={showSolarCompanies}
                                updateCompany={(company) => updateSolarCompany(company)}
                                refreshCompanies={() => getSettings()}
                                showSuccessAlert={(text) => showSuccessAlert(text)}
                            />
                        </Grid>
                    </Grid> 
                ) : (
                    <Grid container justifyContent='flex-start'>
                        <Grid item xs={12}>
                            <Box display='flex' justifyContent='center' alignItems='center' style={{height: '20vh'}}>
                                <Loader />
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </Container>
        </>
    );
}

export default Settings